import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  TextField, 
  Checkbox, 
  FormControlLabel, 
  Button, 
  Paper,
  Alert,
  Snackbar
} from '@mui/material';
import { createCategory } from '../categoriesApi';
import { useBranch } from '../BranchContext';
import { useTranslation } from 'react-i18next';

export default function AddCategory() {
  const { t } = useTranslation();
  const [categoryName, setCategoryName] = useState('');
  const [categoriesOrder, setCategoriesOrder] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });
  const { selectedBranch } = useBranch();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedBranch) {
      setNotification({ open: true, message: t('pleaseSelectBranchFirst'), severity: 'warning' });
      return;
    }
    if (!categoryName.trim()) {
      setNotification({ open: true, message: t('categoryNameRequired'), severity: 'error' });
      return;
    }

    setLoading(true);
    try {
      await createCategory({
        CategoryName: categoryName,
        IsActive: isActive,
        BranchId: selectedBranch.id || selectedBranch.Id,
        CategoriesOrder: categoriesOrder ? parseInt(categoriesOrder) : null
      });
      setNotification({ open: true, message: t('categoryAddedSuccess'), severity: 'success' });
      setCategoryName('');
      setCategoriesOrder('');
      setIsActive(true);
    } catch (error) {
      console.error('Error adding category:', error);
      setNotification({ open: true, message: t('failedToAddCategory'), severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseNotification = () => {
    setNotification({ ...notification, open: false });
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        {t('addNewCategory')}
      </Typography>
      <Typography variant="body1" color="textSecondary" paragraph>
        {t('enterNewCategoryDetails')}
      </Typography>

      {!selectedBranch && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          {t('pleaseSelectBranchHeader')}
        </Alert>
      )}

      <Paper sx={{ p: 4, maxWidth: 600 }}>
        <Typography variant="h6" gutterBottom color="primary">
          {t('categoryDetails')}
        </Typography>
        <form onSubmit={handleSubmit}>
          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              label={t('categoryName')}
              value={categoryName}
              onChange={(e) => setCategoryName(e.target.value)}
              required
              variant="outlined"
            />
          </Box>
          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              label={t('CategoriesOrder')}
              value={categoriesOrder}
              onChange={(e) => setCategoriesOrder(e.target.value.replace(/[^0-9]/g, ''))}
              variant="outlined"
              type="number"
              inputProps={{ min: 1 }}
              helperText={t('categoriesOrderHelper')}
            />
          </Box>
          <Box sx={{ mb: 3 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                  color="primary"
                />
              }
              label={t('isActive')}
            />
          </Box>
          <Button 
            type="submit" 
            variant="contained" 
            color="primary" 
            disabled={loading}
            size="large"
          >
            {loading ? t('saving') : t('save')}
          </Button>
        </form>
      </Paper>

      <Snackbar 
        open={notification.open} 
        autoHideDuration={6000} 
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseNotification} severity={notification.severity} sx={{ width: '100%' }}>
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
